import { isTextElement, TEXT_EFFECTS } from "@excalidraw/element";

import { definePreset, kf, setStatic, timing } from "./helpers";
import { getAbsolutePathPoints, isPathElement } from "./spatial";

import type { AnimationPreset, ParamSchema } from "./types";

const STAGGER_SCHEMA: ParamSchema = {
  key: "stagger",
  label: "Stagger",
  min: 0,
  max: 0.9,
  step: 0.05,
};

const INTENSITY_SCHEMA: ParamSchema = {
  key: "intensity",
  label: "Intensity",
  min: 0.1,
  max: 3,
  step: 0.1,
};

const SPLIT_SCHEMA: ParamSchema = {
  key: "split",
  label: "Split by",
  min: 0,
  max: 2,
  step: 1,
  options: ["Chars", "Words", "Lines"],
};

/** Builds a per-character effect: sets the static effect channels on the
 * target and tweens `textProgress` 0 → 1 — the text renderer derives every
 * glyph's transform/alpha from that single value. */
const charEffect =
  (effectId: number): AnimationPreset["build"] =>
  (tl, target, _element, cfg) => {
    setStatic(tl, target, {
      textEffect: effectId,
      textStagger: cfg.params.stagger ?? 0.6,
      textIntensity: cfg.params.intensity ?? 1,
      textSplit: Math.round(cfg.params.split ?? 0),
    });
    tl.fromTo(
      target,
      { textProgress: 0 },
      { textProgress: 1, ...timing(cfg) },
      0,
    );
  };

const charInPreset = (
  id: string,
  name: string,
  effectId: number,
  opts: { duration?: number; easing?: string } = {},
): AnimationPreset =>
  definePreset(
    id,
    name,
    "text",
    {
      params: { stagger: 0.6, intensity: 1, split: 0 },
      schema: [STAGGER_SCHEMA, INTENSITY_SCHEMA, SPLIT_SCHEMA],
      duration: opts.duration ?? 1500,
      easing: opts.easing ?? "none",
    },
    charEffect(effectId),
  );

/** Exit twin of charInPreset: same per-glyph effect played 1 → 0, so the
 * entrance retraces out (last glyph leaves first — standard reverse). */
const charOutPreset = (
  id: string,
  name: string,
  effectId: number,
  opts: { duration?: number; easing?: string } = {},
): AnimationPreset =>
  definePreset(
    id,
    name,
    "text",
    {
      params: { stagger: 0.6, intensity: 1, split: 0 },
      schema: [STAGGER_SCHEMA, INTENSITY_SCHEMA, SPLIT_SCHEMA],
      duration: opts.duration ?? 1500,
      easing: opts.easing ?? "none",
    },
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        textEffect: effectId,
        textStagger: cfg.params.stagger ?? 0.6,
        textIntensity: cfg.params.intensity ?? 1,
        textSplit: Math.round(cfg.params.split ?? 0),
      });
      tl.fromTo(
        target,
        { textProgress: 1 },
        { textProgress: 0, ...timing(cfg) },
        0,
      );
    },
  );

const charLoopPreset = (
  id: string,
  name: string,
  effectId: number,
): AnimationPreset =>
  definePreset(
    id,
    name,
    "text",
    {
      params: { stagger: 0.6, intensity: 1, split: 0 },
      schema: [STAGGER_SCHEMA, INTENSITY_SCHEMA, SPLIT_SCHEMA],
      duration: 2000,
      repeat: 2,
      easing: "none",
    },
    charEffect(effectId),
  );

export const textPresets: AnimationPreset[] = [
  definePreset(
    "textOnPath",
    "Text on Path",
    "text",
    {
      duration: 3000,
      easing: "none",
    },
    // glyphs ride a drawn line/arrow/freedraw path (selected together, like
    // Follow Path); progress slides the run along the path's slack. Without
    // a path, a gentle arc — the panel-preview fallback.
    (tl, target, element, cfg, sceneElements) => {
      let path = sceneElements?.find(
        (el) => el.id === cfg.pathElementId && isPathElement(el),
      );
      if (!path && sceneElements) {
        const candidates = sceneElements.filter(
          (el) => el.id !== element.id && isPathElement(el),
        );
        const cx = element.x + element.width / 2;
        const cy = element.y + element.height / 2;
        const centerDist = (el: typeof candidates[number]) =>
          Math.hypot(el.x + el.width / 2 - cx, el.y + el.height / 2 - cy);
        path = candidates.sort((a, b) => centerDist(a) - centerDist(b))[0];
      }
      const points =
        path && isPathElement(path)
          ? getAbsolutePathPoints(
              path as Parameters<typeof getAbsolutePathPoints>[0],
            )
          : Array.from({ length: 33 }, (_, i) => {
              const t = i / 32;
              return [t * 300, -40 * Math.sin(Math.PI * t)] as [number, number];
            });
      // cap density so the baked channel stays small in scene data
      const stride = Math.max(1, Math.ceil(points.length / 256));
      const outline: number[] = [];
      for (let i = 0; i < points.length; i += stride) {
        outline.push(points[i][0], points[i][1]);
      }
      setStatic(tl, target, { textPathOutline: outline });
      tl.fromTo(
        target,
        { textPathProgress: 0 },
        { textPathProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "countUp",
    "Counter",
    "text",
    {
      params: { from: 0, to: 100, decimals: 0 },
      schema: [
        { key: "from", label: "From", min: -100000, max: 100000, step: 1 },
        { key: "to", label: "To", min: -100000, max: 100000, step: 1 },
        { key: "decimals", label: "Decimals", min: 0, max: 4, step: 1 },
      ],
      duration: 1500,
      easing: "power1.out",
    },
    // odometer: the renderer swaps the text element's displayed string for
    // the interpolated number (non-text elements are unaffected)
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        counterFrom: cfg.params.from ?? 0,
        counterTo: cfg.params.to ?? 100,
        counterDecimals: Math.round(cfg.params.decimals ?? 0),
      });
      tl.fromTo(
        target,
        { counterProgress: 0 },
        { counterProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  // ------------------------------------------------ per-character, entrance
  charInPreset("charsFadeIn", "Chars Fade In", TEXT_EFFECTS.fadeIn),
  charInPreset("charsSlideUp", "Chars Slide Up", TEXT_EFFECTS.slideUp),
  charInPreset("charsSlideDown", "Chars Slide Down", TEXT_EFFECTS.slideDown),
  charInPreset("charsZoomIn", "Chars Zoom In", TEXT_EFFECTS.zoomIn),
  charInPreset("charsRotateIn", "Chars Rotate In", TEXT_EFFECTS.rotateIn),
  charInPreset("charsFlipIn", "Chars Flip In", TEXT_EFFECTS.flipIn),
  charInPreset("charsBounceIn", "Chars Bounce In", TEXT_EFFECTS.bounceIn),
  charInPreset("charsDropIn", "Chars Drop In", TEXT_EFFECTS.dropIn, {
    duration: 2000,
  }),
  charInPreset("charsScramble", "Scramble", TEXT_EFFECTS.scramble, {
    duration: 2000,
  }),
  charInPreset("charsTypewriter", "Typewriter Chars", TEXT_EFFECTS.typewriter, {
    duration: 2000,
  }),
  charInPreset("charsTrackingIn", "Tracking In", TEXT_EFFECTS.trackingIn, {
    easing: "power3.out",
  }),

  // ---------------------------------------------------- per-character, exit
  // named by what the viewer sees: reversed slideDown (entered from above)
  // exits upward, so it is "Slide Out Up"
  charOutPreset("charsFadeOut", "Chars Fade Out", TEXT_EFFECTS.fadeIn),
  charOutPreset(
    "charsSlideOutUp",
    "Chars Slide Out Up",
    TEXT_EFFECTS.slideDown,
  ),
  charOutPreset(
    "charsSlideOutDown",
    "Chars Slide Out Down",
    TEXT_EFFECTS.slideUp,
  ),
  charOutPreset("charsZoomOut", "Chars Zoom Out", TEXT_EFFECTS.zoomIn),
  charOutPreset("charsRotateOut", "Chars Rotate Out", TEXT_EFFECTS.rotateIn),
  charOutPreset("charsFlipOut", "Chars Flip Out", TEXT_EFFECTS.flipIn),
  charOutPreset("charsBounceOut", "Chars Bounce Out", TEXT_EFFECTS.bounceIn),
  charOutPreset("charsUntype", "Untype", TEXT_EFFECTS.typewriter, {
    duration: 2000,
  }),
  charOutPreset("charsTrackingOut", "Tracking Out", TEXT_EFFECTS.trackingIn, {
    easing: "power3.in",
  }),

  // ---------------------------------------------------- per-character, loop
  charLoopPreset("charsWave", "Chars Wave", TEXT_EFFECTS.wave),
  charLoopPreset("charsJump", "Chars Jump", TEXT_EFFECTS.jump),
  charLoopPreset("charsJitter", "Chars Jitter", TEXT_EFFECTS.jitter),
  charLoopPreset("charsSwing", "Chars Swing", TEXT_EFFECTS.swing),
  charLoopPreset("charsPulse", "Chars Pulse", TEXT_EFFECTS.pulse),

  // ------------------------------------------------------ whole-element text
  definePreset(
    "typewriter",
    "Typewriter",
    "text",
    { duration: 2000 },
    (tl, target, element, cfg) => {
      // step once per character so the reveal advances letter-by-letter
      const steps = isTextElement(element)
        ? Math.max(element.text.length, 1)
        : 12;
      setStatic(tl, target, { revealDir: 0 }); // grow from the left
      tl.fromTo(
        target,
        { revealProgress: 0 },
        {
          revealProgress: 1,
          // full clip timing (yoyo/repeatDelay included), but the per-letter
          // stepping replaces the user ease — a smooth ease would defeat it
          ...timing(cfg),
          ease: `steps(${Math.min(steps, 120)})`,
        },
        0,
      );
    },
  ),
  definePreset(
    "textBlurIn",
    "Focus In",
    "text",
    {
      params: { blur: 8, rise: 12 },
      schema: [
        { key: "blur", label: "Blur px", min: 1, max: 50, step: 1 },
        { key: "rise", label: "Rise px", min: 0, max: 100, step: 1 },
      ],
      easing: "power2.out",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { blur: cfg.params.blur ?? 8, dy: cfg.params.rise ?? 12, opacity: 0 },
        { blur: 0, dy: 0, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "textPopIn",
    "Pop In",
    "text",
    {
      params: { from: 0.3 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "back.out",
    },
    (tl, target, _element, cfg) => {
      const from = cfg.params.from ?? 0.3;
      tl.fromTo(
        target,
        { scaleX: from, scaleY: from, opacity: 0 },
        { scaleX: 1, scaleY: 1, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "textWave",
    "Wave",
    "text",
    {
      params: { amplitude: 8 },
      schema: [
        { key: "amplitude", label: "Amplitude", min: 1, max: 50, step: 1 },
      ],
      repeat: 2,
    },
    (tl, target, _element, cfg) => {
      const a = cfg.params.amplitude ?? 8;
      tl.to(target, kf(cfg, { dy: [0, -a, 0, a, 0] }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "textFlicker",
    "Flicker",
    "text",
    {
      params: { min: 0.2 },
      schema: [
        { key: "min", label: "Min opacity", min: 0, max: 0.9, step: 0.1 },
      ],
    },
    (tl, target, _element, cfg) => {
      const min = cfg.params.min ?? 0.2;
      const mid = Math.min(1, min + 0.4);
      tl.to(
        target,
        kf(cfg, { opacity: [1, min + 0.1, 1, mid, 1, min, 1] }, "none"),
        0,
      );
    },
  ),
];
